/**
 * D-GTCC-VIEW-FIX-PSET-NOTI1 — lets `GtccShell.tsx` hand a view-switching tab
 * bar to `ControlHead` so it renders after the heading, the only tab-bar
 * position the reference uses (`GtccCockpitView.tsx` renders its heading,
 * then its `Tabs`, then panel content). Without this slot the Shell had to
 * stack the tab bar above the view's content, since the view's own
 * `ControlHead` heading is the visible page title and the Shell cannot see
 * inside a view's render to place anything after it.
 *
 * A standalone module (not part of `GtccShell.tsx` or `ControlPrimitives.tsx`)
 * so neither has to import the other: `GtccShell.tsx` already reaches views
 * through `registry.ts`, and those views import `ControlPrimitives.tsx`, so a
 * direct edge back to `GtccShell.tsx` from `ControlPrimitives.tsx` would
 * close a cycle.
 *
 * Only `GtccShell.tsx` provides a value today, for `pset`, `aia` and `mail`
 * (`GTCC_HEAD_TABS_VIEWS`). The provider is keyed by parent, but the active
 * view can be a `parentKey`-only child with no sidebar entry of its own
 * (`dom`/`cat` under `pset`, `ppl` under `aia`, `tg`/`edel` under `mail`) —
 * those five child views read this hook directly, since they have no
 * `ControlHead` call of their own to receive it through. Every other call
 * site reads the default `null` and renders exactly as before.
 */
import { createContext, useContext, type ReactNode } from "react";

const GtccHeadTabsContext = createContext<ReactNode>(null);

export const GtccHeadTabsProvider = GtccHeadTabsContext.Provider;

export function useGtccHeadTabs(): ReactNode {
  return useContext(GtccHeadTabsContext);
}
